import {
  HHButton,
  HHChip,
  HHDivider,
  HHPaper,
  HHStack,
  HHTypography,
} from '@hinge-health/react-component-library';
import { NorthEastOutlined, OpenInNew } from '@mui/icons-material';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { Link } from 'react-router-dom';
import {
  leftPanelAdminPanelAction,
  leftPanelClientInsurerId,
} from '../../constants/strings/left-panel-labels';
import { routes } from '../../constants/strings/routes';
import { BatchClientInsurerType } from '../../types';
import { capitalizeFirstLetterAndSpaceSnakeString } from '../../utils/bills-utils';
import { computeEffectiveRange } from '../../utils/date-helper';
import {
  RecordPanelField,
  RecordPanelMeta,
  RecordPanelTitle,
} from '../utils/record-panel';

export type ClientInsurerWithContract = BatchClientInsurerType & {
  mostExpensiveBillingModelName: string;
  activeContractStartDate: string;
  activeContractEndDate: string | null;
  contractBillingType: string;
  activeContractPartnershipName: string | null;
};

export interface ClientInsurerListProps {
  data: ClientInsurerWithContract[];
}

export const CLIENT_INSURER_CARD_ADMIN_LINK_TEST_ID =
  'client-insurer-card-admin-link';

const EMPTY_VALUE = '--';

const ClientInsurerList = ({ data }: ClientInsurerListProps): JSX.Element => {
  // Do a string search for claims in the billingType string
  const isClaimsBillingType = (billingType: string): boolean =>
    billingType.toLowerCase().includes('claims');

  // Copy before sorting — sort mutates in place, and `data` is a prop.
  const sortedData = [...data].sort(
    (a, b) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0),
  );

  return (
    <HHStack direction="column" spacing={4}>
      <HHTypography hhVariant="h2">Associated Client Insurers</HHTypography>
      {data.length === 0 && (
        <HHTypography hhVariant="muted">No client insurers found</HHTypography>
      )}
      <HHStack display="grid" gridTemplateColumns={'repeat(3, 1fr)'} gap={4}>
        {sortedData.map(clientInsurer => (
          <Link
            to={`${routes.billing.main}/${clientInsurer.id}`}
            style={{ textDecoration: 'none', display: 'flex' }}
            key={clientInsurer.id}
          >
            <HHPaper
              variant="outlined"
              sx={(theme): SystemStyleObject<Theme> => ({
                backgroundColor: theme.palette.background.default,
                borderColor: theme.palette.divider,
                padding: theme.spacing(4),
                display: 'flex',
                flexDirection: 'column',
                flex: 1,
                transition: theme.transitions.create([
                  'border-color',
                  'background-color',
                ]),
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: theme.palette.background.paper,
                },
              })}
            >
              {/* The insurer name is what tells one card from another, so it
                  heads the card rather than sitting in the field list. */}
              <HHStack
                direction="row"
                gap={2}
                justifyContent="space-between"
                alignItems="flex-start"
              >
                <RecordPanelTitle>
                  {clientInsurer.insurer.name}
                </RecordPanelTitle>
                <NorthEastOutlined color="primary" fontSize="small" />
              </HHStack>

              <HHStack
                direction="row"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
                marginTop={2}
              >
                <HHChip
                  label={clientInsurer.isActive ? 'Active' : 'Inactive'}
                  hhVariant="filled"
                  size="small"
                  color={clientInsurer.isActive ? 'primary' : 'default'}
                />
                <RecordPanelMeta
                  label={leftPanelClientInsurerId}
                  value={clientInsurer.id.toString()}
                />
              </HHStack>

              <HHDivider sx={{ marginY: 4 }} />

              <HHStack
                display="grid"
                gridTemplateColumns="repeat(2, 1fr)"
                gap={4}
              >
                <RecordPanelField
                  label="Partnership"
                  value={
                    clientInsurer.activeContractPartnershipName ?? EMPTY_VALUE
                  }
                />
                <RecordPanelField
                  label="Term date"
                  value={clientInsurer.termDate ?? EMPTY_VALUE}
                />
                <RecordPanelField
                  label="Contract dates"
                  value={
                    clientInsurer.activeContractStartDate
                      ? computeEffectiveRange(
                          clientInsurer.activeContractStartDate,
                          clientInsurer.activeContractEndDate,
                        )
                      : EMPTY_VALUE
                  }
                />
                <RecordPanelField
                  label="Billing model"
                  value={
                    clientInsurer.mostExpensiveBillingModelName
                      ? capitalizeFirstLetterAndSpaceSnakeString(
                          clientInsurer.mostExpensiveBillingModelName,
                        )
                      : EMPTY_VALUE
                  }
                />
                <RecordPanelField
                  label="Billing type"
                  value={
                    clientInsurer.contractBillingType ? (
                      <HHChip
                        label={capitalizeFirstLetterAndSpaceSnakeString(
                          clientInsurer.contractBillingType,
                        )}
                        hhVariant="filled"
                        size="small"
                        color={
                          isClaimsBillingType(clientInsurer.contractBillingType)
                            ? 'warning'
                            : 'info'
                        }
                      />
                    ) : (
                      EMPTY_VALUE
                    )
                  }
                />
              </HHStack>

              <HHButton
                hhVariant="variant-bypass"
                variant="text"
                size="small"
                color="primary"
                data-testid={`${CLIENT_INSURER_CARD_ADMIN_LINK_TEST_ID}-${clientInsurer.id}`}
                endIcon={<OpenInNew sx={{ fontSize: 13 }} />}
                sx={{
                  alignSelf: 'flex-start',
                  // Pushes the action to the card's bottom edge so the links
                  // line up across cards of differing content height.
                  marginTop: 'auto',
                  padding: 0,
                  paddingTop: 5,
                  minWidth: 0,
                  fontSize: 13,
                  textTransform: 'none',
                }}
                onClick={(e): void => {
                  e.preventDefault();
                  e.stopPropagation();
                  const baseAdminPanelUrl =
                    process.env.REACT_APP_ADMIN_PANEL_URL || '';

                  window.open(
                    `${baseAdminPanelUrl}/admin/clients_insurers/${clientInsurer.id}`,
                    '_blank',
                  );
                }}
              >
                {leftPanelAdminPanelAction}
              </HHButton>
            </HHPaper>
          </Link>
        ))}
      </HHStack>
    </HHStack>
  );
};

export default ClientInsurerList;
